import type { Session, TenantBundle } from './types';

/**
 * Push contract.
 *
 * Two kinds of notification, and they are handled differently on purpose:
 *
 *   1. Broadcast — the CMS sends these ("Runway entries close in 20 minutes").
 *      The device registers a token against a set of topics; the CMS fans out.
 *   2. Reminder — the app schedules these locally from starred sessions, so a
 *      phone in airplane mode in a basement exhibit hall still buzzes on time.
 */

export type PushProvider = 'expo' | 'webpush';

export interface DeviceRegistration {
  token: string;
  provider: PushProvider;
  tenantId: string;
  platform: 'ios' | 'android' | 'web';
  /** Topics the attendee is opted into: schedule, announcements, offers. */
  topics: string[];
  locale: string;
}

export interface ScheduledReminder {
  id: string;
  sessionId: string;
  title: string;
  body: string;
  /** Epoch ms. */
  fireAt: number;
}

export const DEFAULT_TOPICS = ['announcements', 'schedule-changes'];

/** POST the device token so the CMS can target this attendee. */
export async function registerDevice(
  baseUrl: string,
  reg: DeviceRegistration,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  try {
    const res = await fetchImpl(`${baseUrl.replace(/\/$/, '')}/push/register`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(reg),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Turns starred sessions into local reminders, `reminderLeadMinutes` ahead of
 * each start. Past sessions are dropped so a late install does not fire a burst.
 */
export function buildReminders(
  bundle: TenantBundle,
  savedIds: string[],
  now: Date = new Date(),
): ScheduledReminder[] {
  const lead = bundle.event.reminderLeadMinutes;
  const out: ScheduledReminder[] = [];

  for (const id of savedIds) {
    const session: Session | undefined = bundle.sessions.find((s) => s.id === id);
    if (!session) continue;
    const day = bundle.days.find((d) => d.key === session.day);
    if (!day) continue;

    const start = new Date(`${day.date}T${session.t}:00`);
    const fireAt = start.getTime() - lead * 60_000;
    if (!Number.isFinite(fireAt) || fireAt <= now.getTime()) continue;

    out.push({
      id: `reminder-${session.id}`,
      sessionId: session.id,
      title: session.title,
      body: `Starts in ${lead} minutes · ${session.room}`,
      fireAt,
    });
  }

  return out.sort((a, b) => a.fireAt - b.fireAt);
}
